import { Injectable } from '@angular/core';
import { runtimeEnv } from '../config/runtime-env';

const COGNITO_IDP_TARGET_PREFIX = 'AWSCognitoIdentityProviderService';
const TOKEN_STORAGE_KEY = 'transferswap_token';
const REFRESH_TOKEN_STORAGE_KEY = 'transferswap_refresh_token';
const TOKEN_REFRESH_SKEW_MS = 60_000;

export type AuthTokenResult = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private refreshPromise: Promise<string> | null = null;

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  hasAccessToken(): boolean {
    return Boolean(this.getAccessToken());
  }

  clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  persistAuthTokens(
    result: AuthTokenResult,
    options: { preserveRefreshToken?: boolean } = {},
  ): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, result.accessToken);
    if (result.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, result.refreshToken);
    } else if (!options.preserveRefreshToken) {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const token = this.getAccessToken();
    if (!token || !this.isCognitoAccessToken(token)) {
      return token;
    }

    const payload = this.readJwtPayload(token);
    if (!this.isTokenExpiring(payload)) {
      return token;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken || !this.isCognitoConfigured()) {
      return token;
    }

    return this.refreshCognitoAccessToken(refreshToken);
  }

  isCurrentTokenFromCognito(): boolean {
    return this.isCognitoAccessToken(this.getAccessToken());
  }

  private readJwtPayload(token: string | null): any {
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      return JSON.parse(decodeURIComponent(escape(atob(padded))));
    } catch {
      return null;
    }
  }

  private async refreshCognitoAccessToken(refreshToken: string): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.requestCognitoTokenRefresh(refreshToken).finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async requestCognitoTokenRefresh(refreshToken: string): Promise<string> {
    const response = await fetch(this.cognitoEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `${COGNITO_IDP_TARGET_PREFIX}.InitiateAuth`,
      },
      body: JSON.stringify({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: runtimeEnv.cognitoClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      }),
    });

    const body = await this.parseJsonResponse(response);
    if (!response.ok) {
      throw this.toCognitoError(body);
    }

    const result = body?.AuthenticationResult;
    if (!result?.AccessToken) {
      throw {
        code: 'MissingAccessToken',
        message: 'Cognito did not return an access token.',
      };
    }

    this.persistAuthTokens(
      {
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken,
        idToken: result.IdToken,
      },
      { preserveRefreshToken: true },
    );

    return result.AccessToken;
  }

  private isCognitoConfigured(): boolean {
    return Boolean(
      runtimeEnv.cognitoRegion &&
        runtimeEnv.cognitoUserPoolId &&
        runtimeEnv.cognitoClientId,
    );
  }

  private cognitoEndpoint(): string {
    return `https://cognito-idp.${runtimeEnv.cognitoRegion}.amazonaws.com/`;
  }

  private isCognitoAccessToken(token: string | null): boolean {
    const payload = this.readJwtPayload(token);
    return (
      payload?.token_use === 'access' &&
      typeof payload?.iss === 'string' &&
      payload.iss.includes('cognito-idp')
    );
  }

  private isTokenExpiring(payload: any): boolean {
    if (typeof payload?.exp !== 'number') {
      return false;
    }
    return payload.exp * 1000 - Date.now() <= TOKEN_REFRESH_SKEW_MS;
  }

  private async parseJsonResponse(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  private toCognitoError(body: any): { code?: string; message?: string } {
    const rawCode = body?.__type || body?.code || body?.Code || '';
    const code = String(rawCode).split('#').pop();
    return {
      code,
      message: body?.message || body?.Message || 'Cognito request failed.',
    };
  }
}
